/**
 * Structured answer for Photo question type: image file token + capture metadata.
 * Stored in answers_to_additional_questions as a JSON object (not a bare string token).
 *
 * Compatible with operator/government AnswerFilePreview via token + optional
 * lat/lng/accuracy/captured_at/timezone/content_type fields, plus time-integrity
 * fields (clock offset, sync age, position timestamp, corrected capture time).
 */

/** Max age of a client↔server clock sync before capture is blocked (ms). */
export const PHOTO_TIME_SYNC_MAX_AGE_MS = 5 * 60 * 1000;

/** Max allowed |corrected_capture_time − server_receipt| after latency allowance. */
export const PHOTO_CLOCK_SKEW_MARGIN_MS = 5 * 60 * 1000;

export type PhotoAnswer = {
  /** GCS answer-file object key from upload endpoint */
  token: string;
  latitude: number;
  longitude: number;
  /** Horizontal accuracy in meters when the browser provides it */
  accuracy_meters: number | null;
  /** ISO-8601 capture timestamp from the device clock (UTC) */
  captured_at: string;
  /** IANA timezone, e.g. America/Los_Angeles */
  timezone: string;
  /** MIME type of the uploaded image (helps answer-file preview) */
  content_type?: string | null;
  /**
   * Device-clock capture time adjusted by the measured server clock offset
   * (approximate server time at shutter).
   */
  captured_at_corrected?: string | null;
  /** ISO-8601 timestamp associated with the geolocation fix, when available */
  position_timestamp?: string | null;
  /** Measured (server_time − device_clock) in milliseconds at last sync */
  clock_offset_ms?: number | null;
  /** Age of the server-time synchronization at shutter, in milliseconds */
  time_sync_age_ms?: number | null;
  /** Server receipt time / skew — filled by the API on successful validation */
  server_received_at?: string | null;
  server_skew_ms?: number | null;
  distance_meters_from_property?: number | null;
};

export type GeoPosition = {
  latitude: number;
  longitude: number;
  accuracy_meters: number | null;
  /** GeolocationPosition.timestamp (ms since epoch); typically device-clock based */
  position_timestamp_ms: number | null;
};

export type ClockSyncState = {
  /** server_unix_ms − mid_request_device_ms */
  clock_offset_ms: number;
  /** Device Date.now() when the sync completed */
  synced_at_device_ms: number;
  /** Server unix ms at mid-request */
  server_unix_ms: number;
  /** Round-trip time of the sync request */
  rtt_ms: number;
};

export type ServerTimeResponse = {
  server_time: string;
  unix_ms: number;
};

export type PhotoCaptureIntegrity = {
  clock_offset_ms: number;
  time_sync_age_ms: number;
  position_timestamp_ms: number | null;
};

export function isPhotoAnswer(value: unknown): value is PhotoAnswer {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    typeof v.token === "string" &&
    v.token.length > 0 &&
    typeof v.latitude === "number" &&
    Number.isFinite(v.latitude) &&
    typeof v.longitude === "number" &&
    Number.isFinite(v.longitude) &&
    (v.accuracy_meters === null ||
      (typeof v.accuracy_meters === "number" &&
        Number.isFinite(v.accuracy_meters))) &&
    typeof v.captured_at === "string" &&
    v.captured_at.length > 0 &&
    typeof v.timezone === "string" &&
    v.timezone.length > 0
  );
}

/** Normalize single-photo or multi-photo answers to an array (backward compatible). */
export function normalizePhotoAnswers(value: unknown): PhotoAnswer[] {
  if (isPhotoAnswer(value)) return [value];
  if (!Array.isArray(value)) return [];
  return value.filter(isPhotoAnswer);
}

export function hasPhotoAnswers(value: unknown): boolean {
  return normalizePhotoAnswers(value).length > 0;
}

/** Persist shape: single object when one photo; array when multiple. */
export function serializePhotoAnswers(
  photos: PhotoAnswer[],
  allowMultiple: boolean,
): PhotoAnswer | PhotoAnswer[] | null {
  if (photos.length === 0) return null;
  if (allowMultiple) return photos;
  return photos[0] ?? null;
}

export function buildPhotoAnswer(
  token: string,
  position: GeoPosition,
  capturedAt: Date = new Date(),
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  integrity?: PhotoCaptureIntegrity | null,
): PhotoAnswer {
  const clockOffsetMs = integrity?.clock_offset_ms ?? null;
  const capturedAtCorrected =
    clockOffsetMs != null && Number.isFinite(clockOffsetMs)
      ? new Date(capturedAt.getTime() + clockOffsetMs).toISOString()
      : null;
  const positionTimestamp =
    integrity?.position_timestamp_ms != null &&
    Number.isFinite(integrity.position_timestamp_ms)
      ? new Date(integrity.position_timestamp_ms).toISOString()
      : position.position_timestamp_ms != null &&
          Number.isFinite(position.position_timestamp_ms)
        ? new Date(position.position_timestamp_ms).toISOString()
        : null;

  return {
    token,
    latitude: position.latitude,
    longitude: position.longitude,
    accuracy_meters: position.accuracy_meters,
    captured_at: capturedAt.toISOString(),
    timezone,
    content_type: "image/jpeg",
    captured_at_corrected: capturedAtCorrected,
    position_timestamp: positionTimestamp,
    clock_offset_ms: clockOffsetMs,
    time_sync_age_ms:
      integrity?.time_sync_age_ms != null &&
      Number.isFinite(integrity.time_sync_age_ms)
        ? integrity.time_sync_age_ms
        : null,
  };
}

/**
 * NTP-lite clock sync: measure RTT and compute offset so
 * device_now + offset ≈ server_now.
 */
export async function syncClockWithServer(
  fetchServerTime: () => Promise<ServerTimeResponse>,
): Promise<ClockSyncState> {
  const t0 = Date.now();
  const server = await fetchServerTime();
  const t1 = Date.now();
  if (typeof server.unix_ms !== "number" || !Number.isFinite(server.unix_ms)) {
    throw new Error("Server time response is invalid.");
  }
  const rtt_ms = Math.max(0, t1 - t0);
  const midDeviceMs = t0 + rtt_ms / 2;
  return {
    clock_offset_ms: server.unix_ms - midDeviceMs,
    synced_at_device_ms: t1,
    server_unix_ms: server.unix_ms,
    rtt_ms,
  };
}

export function clockSyncAgeMs(
  sync: ClockSyncState,
  nowDeviceMs: number = Date.now(),
): number {
  return Math.max(0, nowDeviceMs - sync.synced_at_device_ms);
}

export function isClockSyncFresh(
  sync: ClockSyncState | null | undefined,
  nowDeviceMs: number = Date.now(),
  maxAgeMs: number = PHOTO_TIME_SYNC_MAX_AGE_MS,
): boolean {
  if (!sync) return false;
  return clockSyncAgeMs(sync, nowDeviceMs) <= maxAgeMs;
}

export type GeoPermissionState =
  | "prompt"
  | "granted"
  | "denied"
  | "unsupported"
  | "unavailable";

/**
 * Read current position. Rejects when permission denied or position unavailable.
 * Includes GeolocationPosition.timestamp (typically device-clock based).
 */
export function getCurrentGeoPosition(
  options?: PositionOptions,
): Promise<GeoPosition> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("Geolocation is not supported on this device."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy_meters:
            typeof pos.coords.accuracy === "number" &&
            Number.isFinite(pos.coords.accuracy)
              ? pos.coords.accuracy
              : null,
          position_timestamp_ms:
            typeof pos.timestamp === "number" && Number.isFinite(pos.timestamp)
              ? pos.timestamp
              : null,
        });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          reject(new Error("Location permission denied."));
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          reject(new Error("Location is currently unavailable."));
        } else if (err.code === err.TIMEOUT) {
          reject(new Error("Timed out waiting for location."));
        } else {
          reject(new Error(err.message || "Unable to get location."));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 20_000,
        maximumAge: 5_000,
        ...options,
      },
    );
  });
}

/**
 * Prefer rear camera when available; fall back to any video input.
 */
export async function openCameraStream(): Promise<MediaStream> {
  if (
    typeof navigator === "undefined" ||
    !navigator.mediaDevices?.getUserMedia
  ) {
    throw new Error("Camera is not supported in this browser.");
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: "environment" },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    });
  } catch {
    // Some devices reject facingMode constraints; retry with a plain video track.
    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: true,
    });
  }
}

export function captureFrameToJpegBlob(
  video: HTMLVideoElement,
  quality = 0.92,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      reject(new Error("Camera preview is not ready yet."));
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      reject(new Error("Unable to capture photo."));
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to encode photo."));
          return;
        }
        resolve(blob);
      },
      "image/jpeg",
      quality,
    );
  });
}

export function photoBlobToFile(
  blob: Blob,
  capturedAt: Date = new Date(),
): File {
  const stamp = capturedAt.toISOString().replace(/[:.]/g, "-");
  return new File([blob], `inspection-photo-${stamp}.jpg`, {
    type: "image/jpeg",
  });
}
